import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { ConditionsType, NodeType, PlatformType } from '../../../app/types';
import {
  addAnyConditionsNode,
  addEmojiConditionsNode,
  addKeywordConditionsNode,
} from '../../../features/mainSlice';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
    },
    option: {
      marginRight: 36,
    },
    radioInput: {
      fontSize: 15,
    },
    type: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$13,
    },
    disabled: {
      color: `${color.$9} !important`,
      cursor: 'not-allowed',
    },
    disabledLabel: {
      color: `${color.$10} !important`,
      cursor: 'not-allowed',
    },
  },
  {
    name: 'Type',
  },
);

const Type = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode, data } = useSelector(state => state.ma);
  const { features } = useSelector(store => store.organization);
  const { nodes = [], platform } = data || {};
  const { type, source } = selectedNode?.data;
  const sourceNode = (nodes || []).find(n => n?.id === source);

  const isMessage = sourceNode?.type === NodeType.MESSAGE;
  const { enable } = features?.ma_sticker || {};

  const canAddCondition = useMemo(
    () =>
      (sourceNode?.data?.messages || []).some(m => {
        return (m?.data?.elements || []).some(e => {
          return (e?.buttons || []).some(button => {
            const isPhone = button?.type === 'phone';
            const isNotHttpUrl =
              button?.type === 'url' && !/(http(s?)):\/\//i.test(button?.data);
            const isLocation = button?.type === 'location';
            return !isPhone && !isNotHttpUrl && !isLocation;
          });
        });
      }),
    [sourceNode],
  );

  const onChange = value => {
    if (value !== type) {
      if (value === ConditionsType.CLICK && isMessage && canAddCondition) {
        dispatch(addAnyConditionsNode(source));
      } else if (value === ConditionsType.KEYWORD) {
        dispatch(addKeywordConditionsNode(source));
      } else {
        dispatch(addEmojiConditionsNode(source));
      }
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>{t('condition-setting.type')}</div>
      <FormControl component="fieldset">
        <RadioGroup
          aria-label="default-display"
          name="default-display"
          value={type}
          onChange={e => {
            e.stopPropagation();
            onChange(e?.target?.value);
          }}
        >
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={ConditionsType.CLICK}
            control={
              <Radio
                className={classnames({
                  [classes.disabled]: !(isMessage && canAddCondition),
                })}
                color="primary"
              />
            }
            disabled={!(isMessage && canAddCondition)}
            label={
              <div
                className={classnames(classes.type, {
                  [classes.disabledLabel]: !(isMessage && canAddCondition),
                })}
              >
                {t('condition-setting.typeClick')}
              </div>
            }
          />
          {platform === PlatformType.LINE && enable && (
            <FormControlLabel
              className={classes.option}
              classes={{
                label: classes.radioInput,
              }}
              value={ConditionsType.STICKER}
              control={<Radio color="primary" />}
              label={
                <div className={classes.type}>
                  {t('condition-setting.typeSticker')}
                </div>
              }
            />
          )}
          <FormControlLabel
            className={classes.option}
            classes={{
              label: classes.radioInput,
            }}
            value={ConditionsType.KEYWORD}
            control={<Radio color="primary" />}
            label={
              <div className={classes.type}>
                {t('condition-setting.typeKeyword')}
              </div>
            }
          />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

export default Type;
